import { useState, useEffect, useCallback } from 'react';

interface TimeLeft {
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
  isExpired: boolean;
}

export function useReservationTimer(expiresAt: string | null) {
  const calculateTimeLeft = useCallback((): TimeLeft => {
    if (!expiresAt) {
      return { hours: 0, minutes: 0, seconds: 0, totalSeconds: 0, isExpired: true };
    }

    const expiration = new Date(expiresAt).getTime();
    const now = new Date().getTime();
    const difference = expiration - now;

    if (difference <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, totalSeconds: 0, isExpired: true };
    }

    const totalSeconds = Math.floor(difference / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      hours,
      minutes,
      seconds,
      totalSeconds,
      isExpired: false
    };
  }, [expiresAt]);

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft());

  useEffect(() => {
    if (!expiresAt) {
      setTimeLeft({ hours: 0, minutes: 0, seconds: 0, totalSeconds: 0, isExpired: true });
      return;
    }

    // Aggiorna immediatamente
    setTimeLeft(calculateTimeLeft());

    // Aggiorna ogni secondo
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft.isExpired) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt, calculateTimeLeft]);

  const formatTime = useCallback((): string => {
    if (timeLeft.isExpired) return 'Scaduta';
    
    const parts: string[] = [];
    
    if (timeLeft.hours > 0) {
      parts.push(`${timeLeft.hours}h`);
    }
    if (timeLeft.minutes > 0 || timeLeft.hours > 0) {
      parts.push(`${timeLeft.minutes.toString().padStart(2, '0')}m`);
    }
    parts.push(`${timeLeft.seconds.toString().padStart(2, '0')}s`);
    
    return parts.join(' ');
  }, [timeLeft]);

  const formatShort = useCallback((): string => {
    if (timeLeft.isExpired) return 'Scaduta';
    
    if (timeLeft.hours > 0) {
      return `${timeLeft.hours}h ${timeLeft.minutes}m`;
    }
    return `${timeLeft.minutes}m ${timeLeft.seconds}s`;
  }, [timeLeft]);

  return {
    timeLeft,
    formatTime,
    formatShort,
    isExpired: timeLeft.isExpired,
    isExpiringSoon: timeLeft.totalSeconds < 300 // Meno di 5 minuti
  };
}
